//! Log Viewer - Log-Anzeige-Komponente

import { DocumentDuplicateIcon, TrashIcon } from "@heroicons/react/24/outline";
import { forwardRef, useState } from "react";

import { useDebugSystem } from "../../hooks/useDebugSystem";
import { useLogStore } from "../../hooks/useLogStore";
import { formatTimestamp } from "../../utils/logHelpers";
import { LogEntryItem } from "../LogEntry/LogEntryItem";

/**
 * WHAT:
 *   Main log viewer component displaying filtered logs in a scrollable list.
 *
 * WHY:
 *   Provides scrollable interface for viewing log entries with expand/collapse functionality.
 *
 * PROPS:
 *   - ref: React.Ref<HTMLDivElement> — Forwarded ref for external scroll control
 *
 * BEHAVIOR:
 *   - Displays filtered logs from useLogStore hook
 *   - Supports expand/collapse for individual log entries
 *   - Provides copy-to-clipboard functionality
 *   - Shows loading state while logs are being fetched
 *   - Integrates with useDebugSystem for log clearing
 *
 * RISKS:
 *   - NOTE: Expand state is managed locally - may not persist across filter changes
 *   - NOTE: Large log lists may cause performance issues
 *   - WARN: Auto-scroll behavior may interrupt user reading
 *   - NOTE: Copy functionality may fail in some browsers
 */
export const LogViewer = forwardRef<HTMLDivElement>((_properties, reference) => {
  const { filteredLogs, isLoading } = useLogStore();
  const { clearLogs, showContext, showTimestamps } = useDebugSystem();
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  const toggleExpanded = (logId: string) => {
    setExpandedLogs((previous) => {
      const newSet = new Set(previous);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const copyAllLogs = () => {
    const logText = filteredLogs
      .map(
        (log) =>
          `${formatTimestamp(log.timestamp)} [${log.level.toUpperCase()}] ${log.domain}: ${log.message}`,
      )
      .join("\n");

    void navigator.clipboard.writeText(logText);
  };

  const copyAllLogsWithRaw = () => {
    const logText = filteredLogs
      .map(
        (log) =>
          `${formatTimestamp(log.timestamp)} [${log.level.toUpperCase()}] ${log.domain}: ${log.message}\n${JSON.stringify(log, null, 2)}`,
      )
      .join("\n\n---\n\n");

    void navigator.clipboard.writeText(logText);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-400">Lade Logs...</p>
        </div>
      </div>
    );
  }

  if (filteredLogs.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-sm text-gray-400">Keine Logs gefunden</p>
          <p className="text-xs text-gray-500 mt-1">Ändere die Filter oder warte auf neue Logs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b border-gray-700 bg-gray-800 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <span className="text-xs font-medium text-gray-300">{filteredLogs.length} Logs</span>
        </div>

        <div className="flex items-center space-x-1">
          <button
            className="p-1 text-gray-400 hover:text-white transition-colors"
            onClick={copyAllLogs}
            title="Alle Logs kopieren"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
          </button>

          <button
            className="p-1 text-gray-400 hover:text-white transition-colors"
            onClick={copyAllLogsWithRaw}
            title="Alle Logs mit Raw Response kopieren"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
          </button>

          <button
            className="p-1 text-gray-400 hover:text-white transition-colors"
            onClick={() => {
              void clearLogs();
            }}
            title="Logs löschen"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Log List */}
      <div
        className="flex-1 overflow-y-auto bg-gray-900 min-h-0"
        ref={reference}
        style={{ scrollBehavior: "smooth" }}
      >
        <div className="space-y-1 p-2">
          {filteredLogs.map((log) => (
            <LogEntryItem
              isExpanded={expandedLogs.has(log.id)}
              key={log.id}
              log={log}
              onToggleExpanded={() => {
                toggleExpanded(log.id);
              }}
              showContext={showContext}
              showTimestamp={showTimestamps}
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-2 border-t border-gray-700 bg-gray-800 flex-shrink-0">
        <div className="text-xs text-gray-400">
          Letzte Aktualisierung: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
});

LogViewer.displayName = "LogViewer";
